import { useState, useEffect, useRef } from 'react';

import useCurrentUser from './useCurrentUser';

type PromoBannerConfig = {
  imageUrl?: string; // Optional: Banner image URL
  text?: string; // Optional: Banner text content
  header?: string; // Optional: Banner header text (e.g., "Anuncio 📢")
  footer?: string; // Optional: Banner footer text (e.g., "Equipo Lidz")
  callToActionUrl?: string; // Call to action URL (if provided, button will be shown)
  callToActionText?: string; // Optional: Custom button text (defaults to "Registrarme Ahora")
  excludedProjectIds: number[]; // Project IDs to exclude. They won't see it.
  includedCountryCodes?: string[]; // Optional: Country codes to include (e.g., ['CL', 'AR'] to show only in Chile and Argentina)
  enabled: boolean; // Enable/disable the banner globally
  startDate?: string; // Optional: When to start showing the banner (ISO string)
  endDate: string; // Optional: When to stop showing the banner (ISO string)
};

const PROMO_BANNER_CONFIG: PromoBannerConfig = {
  imageUrl: '/banner-sales-lab.svg',
  // text: '',
  // header: '📢 Anuncio', // Banner header
  footer: 'Equipo Lidz', // Banner footer
  callToActionUrl: 'https://saleslablidz.lovable.app/',
  callToActionText: 'Conoce Sales Lab', // Custom call-to-action button text
  // Aconcagua, Asist Virtual aconcagua, pocuro, pocuro QA, paz, pazQA, Portland, Interwin, Cek, Ocampo
  excludedProjectIds: [20, 918, 87, 1018, 721, 94, 1200, 1201, 1250, 1287, 1935],
  // Optional: Include specific countries (e.g., ['CL', 'AR'] to show only in Chile and Argentina)
  includedCountryCodes: ['CL'], // Example: show only in Chile
  // Enable/disable the banner globally
  enabled: true,
  startDate: '2026-02-03T00:00:00.000Z', // Optional: When to start showing the banner (ISO string)
  endDate: '2026-03-15T23:59:59.999Z', // Optional: When to stop showing the banner (ISO string)
} as const;

// Validation function to ensure at least one content type is provided
const validatePromoBannerConfig = (config: PromoBannerConfig): boolean => {
  return !!(config.imageUrl || config.text);
};

// Local storage key for tracking banner dismissal
const PROMO_BANNER_DISMISSED_KEY = 'promo-banner-dismissed';

const usePromoBanner = () => {
  const user = useCurrentUser();
  const [isBannerVisible, setIsBannerVisible] = useState(false);
  const previousUserIdRef = useRef<number | null>(null);
  const isInitialMountRef = useRef(true);

  useEffect(() => {
    // Reset banner state first
    setIsBannerVisible(false);

    // Check if user has changed (including logout/login)
    const currentUserId = user?.id || null;
    const hasUserChanged = previousUserIdRef.current !== currentUserId;

    // Only reset banner state if user actually changed (not on initial mount)
    if (hasUserChanged && !isInitialMountRef.current) {
      localStorage.removeItem(PROMO_BANNER_DISMISSED_KEY);
    }

    // Update tracking refs
    previousUserIdRef.current = currentUserId;
    isInitialMountRef.current = false;

    // Don't show banner if it's disabled globally
    if (!PROMO_BANNER_CONFIG.enabled) {
      return;
    }

    // Don't show banner if no content is configured
    if (!validatePromoBannerConfig(PROMO_BANNER_CONFIG)) {
      return;
    }

    // Check if banner has started (if startDate is set)
    if (PROMO_BANNER_CONFIG.startDate) {
      const startDate = new Date(PROMO_BANNER_CONFIG.startDate);
      const now = new Date();
      if (now < startDate) {
        return;
      }
    }

    // Check if banner has expired
    if (PROMO_BANNER_CONFIG.endDate) {
      const endDate = new Date(PROMO_BANNER_CONFIG.endDate);
      const now = new Date();
      if (now >= endDate) {
        return;
      }
    }

    // Don't show banner if user is not logged in
    if (!user) {
      return;
    }

    // Don't show banner if user's projectId is in excluded list
    if (user.projectId && PROMO_BANNER_CONFIG.excludedProjectIds.includes(Number(user.projectId))) {
      return;
    }

    // Don't show banner if user's country is not in included list
    if (
      PROMO_BANNER_CONFIG.includedCountryCodes &&
      PROMO_BANNER_CONFIG.includedCountryCodes.length > 0 &&
      (!user.countryCode || !PROMO_BANNER_CONFIG.includedCountryCodes.includes(user.countryCode))
    ) {
      return;
    }

    // Check if banner was previously dismissed
    const isDismissed = localStorage.getItem(PROMO_BANNER_DISMISSED_KEY) === 'true';
    if (isDismissed) {
      return;
    }

    // Show banner for eligible users
    setIsBannerVisible(true);
  }, [user]);

  const dismissBanner = () => {
    setIsBannerVisible(false);
    // Store dismissal state in localStorage
    localStorage.setItem(PROMO_BANNER_DISMISSED_KEY, 'true');
  };

  return {
    isBannerVisible,
    dismissBanner,
    config: PROMO_BANNER_CONFIG,
  };
};

export default usePromoBanner;
